import React, { useState, useRef, useEffect } from 'react';
import { Container, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faRobot, faUser, faSpinner, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { Link, useParams } from 'react-router-dom';
import { documentsAPI } from '../../services/api';

const ChatWithDocument = () => {
  const { documentId } = useParams();
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [question, setQuestion] = useState('');
  const [conversation, setConversation] = useState([]);
  const [processing, setProcessing] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchDocumentDetails();
  }, [documentId]);

  useEffect(() => {
    scrollToBottom();
  }, [conversation]);

  const fetchDocumentDetails = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await documentsAPI.getDocument(documentId);
      
      if (response.data.status === 200) {
        setDocument(response.data.data.document);
      } else {
        setError('Document not found or you don\'t have permission to access it');
      }
    } catch (err) {
      console.error('Error fetching document:', err);
      setError('Failed to load document details');
    } finally {
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmitQuestion = async (e) => {
    e.preventDefault();
    
    if (!question.trim()) return;
    
    // Add user question to conversation
    const userQuestion = question;
    // Create a new conversation array instead of updating the previous one
    const updatedConversation = [...conversation, { role: 'user', content: userQuestion }];
    setConversation(updatedConversation);
    setQuestion('');
    setProcessing(true);
    
    try {
      // Call API to get answer
      const response = await documentsAPI.chatWithDocument(documentId, userQuestion);
      
      if (response.data.status === 200) {
        // Use the updated conversation array directly instead of prev state
        setConversation([...updatedConversation, { 
          role: 'assistant', 
          content: response.data.data.answer 
        }]);
      } else {
        setConversation([...updatedConversation, { 
          role: 'assistant', 
          content: 'Sorry, I couldn\'t process your question. Please try again.',
          isError: true
        }]);
      }
    } catch (err) {
      console.error('Error processing question:', err);
      setConversation([...updatedConversation, { 
        role: 'assistant', 
        content: 'Sorry, an error occurred while processing your question. Please try again later.',
        isError: true
      }]);
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" role="status" variant="primary" />
        <h3 className="mt-3">Loading document...</h3>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-4">
        <Alert variant="danger">{error}</Alert>
        <Button as={Link} to="/documents" variant="primary">
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Back to Documents
        </Button>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <div className="mb-4">
        <Button as={Link} to={`/documents/${documentId}`} variant="outline-primary" className="me-2">
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Back to Document
        </Button>
      </div>
      
      <Card className="shadow-sm mb-4">
        <Card.Header className="bg-primary text-white">
          <h4 className="mb-0">
            Chat with Document: {document?.filename || document?.file_name || 'Document'}
          </h4>
        </Card.Header>
        <Card.Body>
          <div className="chat-container" style={{ height: '400px', overflowY: 'auto', marginBottom: '1rem' }}>
            {conversation.length === 0 ? (
              <div className="text-center text-muted py-5">
                <h5>Document Q&A</h5>
                <p>Ask questions about this document to extract information or understand its content.</p>
              </div>
            ) : (
              conversation.map((message, index) => (
                <div 
                  key={index} 
                  className="mb-3"
                >
                  <div className="d-flex align-items-center mb-1">
                    <strong className="me-2">{message.role === 'user' ? 'Question:' : 'Answer:'}</strong>
                  </div>
                  <div className={`p-3 ${message.role === 'user' ? 'bg-light' : 'bg-white'} border rounded`}>
                    <div className={message.isError ? 'text-danger' : ''}>
                      {message.content}
                    </div>
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>
          
          <Form onSubmit={handleSubmitQuestion}>
            <Form.Group className="d-flex">
              <Form.Control
                type="text"
                placeholder="Ask a question about this document..."
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                disabled={processing}
                className="me-2"
              />
              <Button 
                type="submit" 
                variant="primary" 
                disabled={processing || !question.trim()}
              >
                {processing ? (
                  <FontAwesomeIcon icon={faSpinner} spin />
                ) : (
                  <FontAwesomeIcon icon={faPaperPlane} />
                )}
              </Button>
            </Form.Group>
          </Form>
        </Card.Body>
      </Card>
      
      <Card className="shadow-sm">
        <Card.Header className="bg-light">
          <h5 className="mb-0">Tips for Document Q&A</h5>
        </Card.Header>
        <Card.Body>
          <ul className="mb-0">
            <li>Ask specific questions about the document content</li>
            <li>For complex documents, break down your questions into smaller parts</li>
            <li>You can ask follow-up questions to get more details</li>
            <li>Clear, concise questions yield better results</li>
          </ul>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default ChatWithDocument;
